<script setup>
import { useVuelidate } from "@vuelidate/core";
import { helpers, requiredIf } from "@vuelidate/validators";
import { computed, reactive, ref, toRaw, toRefs } from "vue";
import { useRouter } from "vue-router";
import ButtonWithSpinner from "@/components/forms/SharedComponents/ButtonWithSpinner.vue";
import CheckBox from "@/components/forms/SharedComponents/DarkBlueInputs/Checkbox.vue";
import OrderDetails from "@/components/OrderDetails/OrderDetails.vue";
import { REGISTRATION_TYPE } from "@/components/program-manager/sessions/constants.js";
import ArrowRight from "@/components/shared/icons/ArrowRight.vue";
import { hasStripeApplicationDepositEnabled } from "@/composables/authorization.js";
import { useDepositModels } from "@/composables/useDepositModels";
import { useNotifications } from "@/composables/useNotifications";
import { useStripe } from "@/composables/useStripe";
import { ORDER_STATUS } from "@/constants";
import { useProfileAddresses } from "../composables/useProfileAddresses";
import { CHECKOUT_SUBMIT_BUTTON_LABELS, RegistrationStage } from "../constants";
import { buildOrderQueryParams } from "../utils/utils";

const props = defineProps({
  // see useApplication.js for application definition
  application: {
    type: Object,
    default: () => {},
    required: true,
  },
  goToNextStep: {
    type: Function,
    default: () => {},
    required: true,
  },
});

const REQUIRED_ERROR_COPY = "This field is required.";
const { application } = toRefs(props);
const router = useRouter();
const reviewSubmit = ref(null);
const pricing = ref(null);
const submissionError = ref("");

const {
  primaryOrder,
  session,
  programPage,
  programPageIsLoading,
  applicationId,
  schoolCollegeId: homeInstitutionId,
  orderIsInDraft,
  withdrawalPolicyUrl,
  requiresCancellationPolicyAgreement,
  isOrderEditable,
  institutionPays,
  eligibleForGroupVisa,
  appliedAgreement,
  profile,
  userData,
  ipeds,
  ownerId,
  customerId,
  participantId,
  submitToPaymentService,
  saveApplicationSubmission,
  updateProfile,
  updateOrder,
  studentFormioPaymentSubmission,
} = application.value;

const { getPaymentServicePayload, getStripeChargePayload, getSubmissionsDataForFormIo } = useDepositModels();
const { sendNotifications } = useNotifications();
const { savePaymentCharge, paymentStatus } = useStripe();

const { currentAddress, permanentAddress, billingAddress } = useProfileAddresses(profile);
const isPersonalized = computed(() => session.value?.is_personalized);
const opeId = computed(() => primaryOrder.value?.ope_id);
// Hide the price breakdown while the program page loads, and once loaded when it is configured not to
// display a starting price. Holding during load avoids briefly flashing prices that then disappear.
const hidePrices = computed(() => programPageIsLoading.value || programPage.value?.display_starting_price === false);

const vuelidateState = reactive({
  cancellationPolicyCheckbox: {
    policy: !orderIsInDraft.value,
  },
});

const vuelidateRules = {
  cancellationPolicyCheckbox: {
    policy: {
      requiredIf: helpers.withMessage(
        REQUIRED_ERROR_COPY,
        requiredIf(() => requiresCancellationPolicyAgreement.value),
      ),
    },
  },
};

const vuelidate = useVuelidate(vuelidateRules, vuelidateState);

const initialPersonalizeLocation = computed(() => {
  if (!primaryOrder.value?.id) return "";

  const location = router.resolve({
    name: "modify-application",
    params: {
      applicationId: applicationId.value,
      stage: RegistrationStage.PERSONALIZE,
    },
    query: buildOrderQueryParams(primaryOrder),
  });

  return location.href;
});

const buttonLabel = computed(() => {
  return session.value?.requires_application
    ? CHECKOUT_SUBMIT_BUTTON_LABELS.application
    : CHECKOUT_SUBMIT_BUTTON_LABELS.registration;
});

const enabledReviewButton = computed(() => {
  if (requiresCancellationPolicyAgreement.value && orderIsInDraft.value) {
    return !vuelidateState.cancellationPolicyCheckbox.policy;
  }
  return false;
});

const submissionData = computed(() => {
  return {
    ipeds: ipeds.value,
    ownerId: ownerId.value,
    formioUserData: userData.value,
    applicationId: applicationId.value,
    customerId: customerId.value,
    participantId: participantId.value,
    studentFormioPaymentSubmission: studentFormioPaymentSubmission.value,
    currentAddress: currentAddress.value,
    permanentAddress: permanentAddress.value,
    billingAddress: billingAddress.value,
  };
});

const submitOrderWithoutApplicationFee = async (cancellationPolicy, submissionContext) => {
  if (!primaryOrder.value?.id) return;

  const FORM_IO_FORM_INSTITUTION = "institutionpayment";
  const FORM_IO_FORM_STUDENT = "paymentform";

  const depositData = {
    person: {
      first_name: submissionContext.formioUserData.data.firstname,
      last_name: submissionContext.formioUserData.data.lastname,
    },
    applicationId: submissionContext.applicationId,
    amount: session.value?.application_fee_in_cents ?? 0,
    productName: "Application Deposit",
    session: session.value,
    userData: submissionContext.formioUserData,
    profile: profile.value,
    ipeds: submissionContext.ipeds,
    ownerId: submissionContext.ownerId,
    billingAddress: submissionContext.billingAddress,
    currentAddress: submissionContext.currentAddress,
    permanentAddress: submissionContext.permanentAddress,
    customerId: submissionContext.customerId,
    hostInstitution: session.value?.session_hosting_institutions?.[0]?.entity?.name ?? "",
    contractNumber: appliedAgreement.value?.contract_number ?? null,
    participantId: submissionContext.participantId,
    cancellationPolicy,
    transactionId: "NoApplicationFeeFlow",
    paymentStatus: "succeeded",
  };

  const submissionsData = getSubmissionsDataForFormIo(depositData);
  if (submissionContext.studentFormioPaymentSubmission) {
    submissionsData._id = submissionContext.studentFormioPaymentSubmission._id;
  }

  const orderPayload = {
    ...toRaw(primaryOrder.value),
    status: ORDER_STATUS.submitted,
    application_fee_payment_id: null,
    billing_contract_id: appliedAgreement.value?.contract_number ?? null,
    ope_id: primaryOrder.value?.ope_id,
    eligible_for_group_visa: eligibleForGroupVisa.value,
    host_institution_id: session.value?.session_hosting_institutions?.[0]?.entity_id,
  };

  if (institutionPays.value) {
    if (hasStripeApplicationDepositEnabled.value) {
      await submitToPaymentService(getPaymentServicePayload(depositData));
    }
    await saveApplicationSubmission(FORM_IO_FORM_INSTITUTION, submissionsData);
    sendNotifications("submit", session.value.salesforce_id);
    await updateOrder(primaryOrder.value.id, orderPayload, "reviewAndSubmit.institutionPays.flow");
  } else {
    await saveApplicationSubmission(FORM_IO_FORM_STUDENT, submissionsData);
    if (hasStripeApplicationDepositEnabled.value) {
      paymentStatus.value = "succeeded"; // no-fee flow: no real Stripe transaction occurs, simulate success
      await savePaymentCharge(getStripeChargePayload(depositData));
      sendNotifications("submit", session.value.salesforce_id);
    }
    await updateOrder(primaryOrder.value.id, orderPayload, "reviewAndSubmit.studentPays.flow");

    // in case student had no billing contract we might set their current address as their billing address
    await updateProfile(submissionContext.participantId, {
      email: profile.value.email,
      manual_billing_address_completion: profile.value?.manual_billing_address_completion,
      billing_address_country_id: submissionContext.billingAddress.country,
      billing_address_state: submissionContext.billingAddress.state,
      billing_address_city: submissionContext.billingAddress.city,
      billing_address_line_1: submissionContext.billingAddress.street_address_1,
      billing_address_postal_code: submissionContext.billingAddress.postal_code,
      billing_address_administrative_area_level_2: profile.value?.billing_address_administrative_area_level_2,
      majors: profile.value.majors ?? [],
      minors: profile.value.minors ?? [],
      colleges: profile.value.colleges ?? [],
      languages: profile.value.languages ?? [],
      parents: profile.value.parents ?? [],
      user_types: profile.value.user_types ?? [],
      okta_id: profile.value.okta_id,
    });
  }
};

const submitReview = async () => {
  try {
    reviewSubmit.value.startLoading();
    if (orderIsInDraft.value) {
      const cancellationPolicy =
        !requiresCancellationPolicyAgreement.value || vuelidateState.cancellationPolicyCheckbox.policy;
      await submitOrderWithoutApplicationFee(cancellationPolicy, submissionData.value);
    }
    props.goToNextStep();
  } catch (e) {
    submissionError.value = e;
  } finally {
    reviewSubmit.value.stopLoading();
  }
};
</script>

<template>
  <div class="text-indigo-base text-left bg-white w-full max-w-full min-w-0 px-4 pb-4 md:px-0">
    <div class="grid grid-cols-1 gap-6 md:gap-7 md:pt-4">
      <div>
        <h1 class="font-bold text-[28px] uppercase" data-testid="review-headline">
          Take a moment to make sure all of your selections look just right!
        </h1>
        <p v-if="isPersonalized && isOrderEditable" class="font-medium text-xl pt-10" data-testid="review-copy">
          <router-link :class="`${secondaryColorClassLinks} font-semibold hover:underline`"
            :to="initialPersonalizeLocation">Go back</router-link>
          if something doesn't look right. <br />
          <br />
          Don't worry, we'll save your progress so you can pick up right where you left off!
        </p>
      </div>

      <div class="flex flex-col w-full py-4 px-6 gap-8 md:m-auto"
        :class="`${secondaryColor}` ? 'bg-gray-275' : 'bg-teal-light'">
        <order-details :current-order="primaryOrder" :is-personalized="isPersonalized" :program-session="session"
          :ope8-id="opeId" :home-institution-id="homeInstitutionId" :hide-prices="hidePrices"
          @get-pricing="(_pricing) => (pricing = _pricing)" />
      </div>

      <div v-if="submissionError" class="error text-error-900 mt-2">
        {{ submissionError }}
      </div>

      <hr />

      <p v-if="requiresCancellationPolicyAgreement" class="mt-2 mb-2 text-gray-700 text-sm">
        To finalize your registration, please acknowledge the withdrawal policy and click submit.
      </p>

      <!-- Cancellation Policy -->
      <check-box v-if="requiresCancellationPolicyAgreement" id="cancellationPolicy" data-testid="cancellationPolicyCheckbox"
        v-model="vuelidate.cancellationPolicyCheckbox.policy.$model"
        :vuelidate-field="vuelidate.cancellationPolicyCheckbox.policy" :is-disabled="!orderIsInDraft">
        <template #label>
          I agree to the
          <a :class="`underline ${secondaryColorClassLinks}`" :href="withdrawalPolicyUrl"
            target="_blank">Withdrawal Policy.</a>*
        </template>
      </check-box>

      <ButtonWithSpinner id="reviewButton" ref="reviewSubmit" data-cy="reviewButton" type="submit" variant="secondary"
        variant-type="block" button-height="min-h-[58px] md:min-h-[60px]" :grey-disabled-class="true"
        :disabled="enabledReviewButton" @click="submitReview">
        <div class="flex items-center justify-center">
          <span class="pr-3 uppercase" data-testid="review-summary-text">
            {{ buttonLabel }}
          </span>
          <ArrowRight custom-class="w-4 h-4" />
        </div>
      </ButtonWithSpinner>
    </div>
  </div>
</template>
